/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * ProductAiInfo.jsx — ข้อมูลสินค้าสำหรับงาน AI   (งานข้อ 1 · Step 1.6)
 *
 * ทำไมถึงไม่เอา field ไปใส่ initAttr ของ Product.jsx ตามแผนแรก
 *   product.con.php:377 บันทึกด้วย update($table, $this->attr) ตรง ๆ
 *   ถ้าส่ง field ของตารางนี้ปนไปกับฟอร์มสินค้า จะกลายเป็น
 *   UPDATE products SET marketing_desc = ... ซึ่งไม่มีคอลัมน์นี้
 *   การบันทึกสินค้าจะพังทั้งหน้า
 *
 *   ไฟล์นี้จึงถือ state ของตัวเอง และบันทึกผ่าน endpoint ของตัวเอง
 *   ไม่แตะฟอร์มสินค้าเดิมแม้แต่ field เดียว
 *
 * อ่าน   OPTIONS /api/product_ai/by_product   { product_id }
 * ร่าง   OPTIONS /api/product_ai/suggest      { product_id } — ดึงจากข้อมูลสินค้าธรรมดา ไม่เรียก AI (เจ้าของงานขอ 6 ต.ค. 2569)
 * บันทึก PUT     /api/product_ai              { product_id, ... }  เป็น upsert
 * แคมเปญ OPTIONS /api/product_ai/campaigns    { product_id } — อ่านอย่างเดียว (ใบเสนอราคาข้อ 1 "แคมเปญที่เกี่ยวข้อง" · 17 ต.ค. 2569)
 *
 * ดีไซน์ (2 ก.ย.): โทนทางการ เรียบ คลีน
 *   กล่องเนื้อหาการตลาดเต็มกว้าง ปุ่มบันทึกท้ายกล่อง
 *   ช่อง "สถานะงานภาพ" / "พร้อมให้ AI ใช้งาน" ซ่อนแล้ว (18 ต.ค. 2569 · ไม่มีระบบส่วนไหนอ่าน ป้ายหัวขัดกับความจริง)
 *   ค่าเดิมในฐานยังถูกส่งกลับตอนบันทึก ไม่หาย
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  product_id: '',
  marketing_desc: '',
  selling_points: '',
  keywords: '',
  image_status: 'NONE',
  ai_ready: 'N',
  enable: 'Y',
  updated_at: '',
};

// ร่างเฉพาะแท็บเบราว์เซอร์ อายุ 8 ชั่วโมง ไม่ใช่การบันทึกฐานข้อมูล
const draftMemory = new Map();
const fields = ['marketing_desc', 'selling_points', 'keywords', 'image_status', 'ai_ready'];
const draftFields = (value) => Object.fromEntries(fields.map((k) => [k, value[k] || initAttr[k]]));
const draftTTL = 8 * 60 * 60 * 1000;

/* รหัส error จาก backend ตอนบันทึก → ข้อความไทย (18 ต.ค. 2569) */
const SAVE_ERROR_TEXT = {
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์แก้ไขข้อมูล',
  product_not_found: 'ไม่พบสินค้านี้แล้ว โหลดหน้าใหม่แล้วลองอีกครั้ง',
  product_id_required: 'ไม่พบสินค้านี้แล้ว โหลดหน้าใหม่แล้วลองอีกครั้ง',
  load_fail: 'โหลดข้อมูลไม่สำเร็จ โหลดหน้าใหม่แล้วลองอีกครั้ง',
};

/* สถานะแคมเปญเทียบวันนี้ — backend คิดจาก CURDATE() ของฐาน */
const campaignState = {
  ACTIVE: { label: 'ใช้อยู่', color: 'success' },
  UPCOMING: { label: 'ยังไม่เริ่ม', color: 'info' },
  EXPIRED: { label: 'หมดอายุแล้ว', color: undefined },
};

/* กรอบส่วนเนื้อหา — เส้นบางสีอ่อน ไม่มีเงา ให้ดูเรียบและเป็นทางการ */
const sectionSx = {
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 2,
  p: 3,
  height: '100%',
  bgcolor: '#ffffff',
};

/* หัวข้อย่อยแบบ eyebrow — ตัวพิมพ์เล็ก เว้นช่องไฟ อ่านเป็นระเบียบ */
const eyebrow = (text) => (
  <MDTypography
    variant='caption'
    fontWeight='bold'
    color='text'
    sx={{ letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}
  >
    {text}
  </MDTypography>
);

function ProductAiInfo() {
  const mod = 'product_ai';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const { id } = useParams();

  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [base, setBase] = useState(initAttr);
  const [loadedKey, setLoadedKey] = useState('');
  const [restored, setRestored] = useState(false);
  const [storageFailed, setStorageFailed] = useState(false);
  const [camps, setCamps] = useState(null);
  const [showExpired, setShowExpired] = useState(false);
  const [confirmBox, setConfirmBox] = useState(null); // กล่องยืนยันแบบเดียวกับหน้าอื่น แทน window.confirm (18 ต.ค. 2569)
  const notifyTimer = useRef(null);
  const alive = useRef(true);
  const requestId = useRef(0);
  const campReq = useRef(0);
  const draftKey = `entrio:ai-info:v1:${app.user.id}:${id}`;
  const dirty =
    loadedKey === draftKey &&
    JSON.stringify(draftFields(attr)) !== JSON.stringify(draftFields(base));

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      requestId.current += 1;
    };
  }, []);

  useEffect(() => {
    if (loadedKey !== draftKey) return;
    const draft = { at: Date.now(), data: draftFields(attr) };
    if (dirty) draftMemory.set(draftKey, draft);
    else draftMemory.delete(draftKey);
    try {
      if (dirty) sessionStorage.setItem(draftKey, JSON.stringify(draft));
      else sessionStorage.removeItem(draftKey);
      setStorageFailed(false);
    } catch (_) {
      setStorageFailed(true);
    }
  }, [attr, base, draftKey, loadedKey, dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    // error ค้างนานให้อ่านทัน · ล้างตัวจับเวลาเก่าไม่ให้ปิดข้อความใหม่ก่อนเวลา (18 ต.ค. 2569)
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  const open = () => {
    setLoading(true);
    requestId.current += 1;
    const req = requestId.current;

    FetchData('OPTIONS', `/${mod}/by_product`, app.token, {
      product_id: id,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current || req !== requestId.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          const saved = { ...initAttr, ...result.data };
          let draft = draftMemory.get(draftKey);
          try {
            draft = JSON.parse(sessionStorage.getItem(draftKey)) || draft;
          } catch (_) {
            /* ใช้ร่างในหน่วยความจำ */
          }
          const valid =
            draft &&
            Date.now() - draft.at < draftTTL &&
            draft.data &&
            fields.every((k) => typeof draft.data[k] === 'string');
          setBase(saved);
          setAttr(valid ? { ...saved, ...draftFields(draft.data) } : saved);
          setRestored(Boolean(valid));
          setLoadedKey(draftKey);
        } else {
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current && req === requestId.current) {
          setLoading(false);
          showNotify(t(`alert.loaderror`), 'error');
        }
      });
  };

  const save = () => {
    if (loading || loadedKey !== draftKey) return;
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      product_id: id,
      marketing_desc: attr.marketing_desc,
      selling_points: attr.selling_points,
      keywords: attr.keywords,
      image_status: attr.image_status,
      ai_ready: attr.ai_ready,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          showNotify(t(`alert.savecomplete`), 'success');
          draftMemory.delete(draftKey);
          try {
            sessionStorage.removeItem(draftKey);
          } catch (_) {
            /* ไม่ลบร่างฐานข้อมูล */
          }
          setBase(attr);
          setRestored(false);
          open();
        } else {
          showNotify(SAVE_ERROR_TEXT[result.msg] || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(t(`alert.saveerror`), 'error');
        }
      });
  };

  /* ดึงจากข้อมูลสินค้าธรรมดา (ชื่อ · รายละเอียด · แบรนด์ · หมวด) มาเป็นร่าง — ยังไม่บันทึกจนกว่าจะกดบันทึก */
  const runSuggest = () => {
    setLoading(true);
    const req = requestId.current;
    FetchData('OPTIONS', `/${mod}/suggest`, app.token, { product_id: id, _t: Date.now() })
      .then((result) => {
        if (!alive.current || req !== requestId.current) return;
        setLoading(false);
        if (result.status === 'ok') {
          setAttr({ ...attr, ...result.data });
          showNotify(
            `ดึงจาก ${
              (result.source || []).join(' · ') || 'ข้อมูลสินค้า'
            } แล้ว ตรวจแก้แล้วกดบันทึก`,
            'info'
          );
        } else {
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      // เน็ตหลุด/HTTP 500 เดิมไม่มี catch → loading ค้าง ฉากมืดบังทั้งจอ (16 ต.ค. 2569)
      .catch(() => {
        if (alive.current && req === requestId.current) {
          setLoading(false);
          showNotify(t(`alert.loaderror`), 'error');
        }
      });
  };

  const suggest = () => {
    // ต้องโหลดข้อมูลที่บันทึกไว้ของสินค้านี้ก่อน ไม่งั้นร่างทับข้อมูลที่ยังไม่เห็น (16 ต.ค. 2569)
    if (loading || loadedKey !== draftKey) return;
    const filled = ['marketing_desc', 'selling_points', 'keywords'].filter(
      (k) => (attr[k] || '').trim() !== ''
    );
    if (filled.length > 0) {
      setConfirmBox({
        title: 'แทนที่ข้อมูลที่มีอยู่?',
        text: 'ช่องที่กรอกไว้จะถูกแทนด้วยข้อความที่ดึงจากข้อมูลสินค้า · ยังไม่บันทึกจนกว่าจะกดบันทึก',
        okLabel: 'แทนที่',
        onOk: runSuggest,
      });
      return;
    }
    runSuggest();
  };
  /* แคมเปญที่เกี่ยวข้อง — โหลดแยกจากข้อมูล AI พังแล้วไม่กระทบการแก้/บันทึก */
  const loadCampaigns = () => {
    campReq.current += 1;
    const req = campReq.current;
    setCamps(null);
    setShowExpired(false);
    FetchData('OPTIONS', `/${mod}/campaigns`, app.token, { product_id: id, _t: Date.now() })
      .then((result) => {
        if (!alive.current || req !== campReq.current) return;
        setCamps(result.status === 'ok' ? result : { failed: true });
      })
      .catch(() => {
        if (alive.current && req === campReq.current) setCamps({ failed: true });
      });
  };

  useEffect(() => {
    setLoadedKey('');
    setRestored(false);
    if (id) {
      open();
      loadCampaigns();
    }
  }, [id, app.user.id]);

  // บันทึกต้องมีสิทธิ์ "แก้ไขข้อมูล" (backend เช็คเหมือนกัน · superadmin ได้ทุกสิทธิ์ตอน login)
  const canEdit = Boolean(app.perm && app.perm.edit);

  const campaignRow = (c) => {
    const st = campaignState[c.state] || campaignState.EXPIRED;
    const branchNames = (c.branches || []).map((b) => b.name).join(', ');
    return (
      <MDBox
        key={c.id}
        display='flex'
        alignItems='center'
        gap={1.5}
        py={1.25}
        flexWrap='wrap'
        sx={{ borderTop: '1px solid', borderColor: 'grey.200' }}
      >
        <Chip
          size='small'
          variant='outlined'
          color={st.color}
          label={st.label}
          sx={{ minWidth: 104 }}
        />
        <MDBox flex='1 1 280px' minWidth={0}>
          <MDTypography variant='button' display='block' noWrap title={c.name}>
            <NavLink to={`/campaign/${c.id}`} style={{ fontWeight: 700 }}>
              {c.code}
            </NavLink>
            &nbsp;&nbsp;{c.name}
          </MDTypography>
        </MDBox>
        {(c.roles || []).includes('FREE') && (
          <Chip size='small' color='warning' variant='outlined' label='ของแถม' />
        )}
        {(c.roles || []).includes('ALL') && (
          <Chip size='small' variant='outlined' label='ทุกสินค้า' />
        )}
        <MDTypography
          variant='button'
          fontWeight='medium'
          sx={{ minWidth: 190 }}
          title={c.type_name}
        >
          {c.deal || c.type_name || '-'}
        </MDTypography>
        <MDTypography variant='caption' color='text' sx={{ minWidth: 150 }}>
          {Func.dateDisplay(c.date_start)} – {Func.dateDisplay(c.date_stop)}
        </MDTypography>
        <MDTypography
          variant='caption'
          color='text'
          sx={{ minWidth: 64 }}
          title={c.all_branch === 'Y' ? undefined : branchNames}
        >
          {c.all_branch === 'Y' ? 'ทุกสาขา' : `${(c.branches || []).length} สาขา`}
        </MDTypography>
      </MDBox>
    );
  };

  const current = camps && !camps.failed ? [...camps.active, ...camps.upcoming] : [];
  const expiredTotal = camps && !camps.failed ? Number(camps.expired_total || 0) : 0;

  return (
    <MDBox pt={1}>
      {/* ── ส่วนหัว ─────────────────────────────────────────────────────── */}
      <MDBox
        display='flex'
        alignItems='flex-start'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1}
      >
        <MDBox>
          <MDTypography variant='h5' fontWeight='medium'>
            ข้อมูลสินค้าสำหรับงาน AI
          </MDTypography>
          <MDTypography variant='body2' color='text' sx={{ opacity: 0.8 }}>
            คำบรรยาย จุดขาย และคำค้นของสินค้า
          </MDTypography>
        </MDBox>
      </MDBox>

      <Divider sx={{ my: 2 }} />
      {dirty && (
        <MDBox mb={2} display='flex' alignItems='center' gap={1} flexWrap='wrap' aria-live='polite'>
          <Chip
            size='small'
            color='warning'
            label={restored ? 'ยังไม่บันทึก · ข้อความที่แก้ค้างไว้กลับมาแล้ว' : 'ยังไม่บันทึก'}
          />
          {storageFailed && (
            <MDTypography variant='caption' color='error'>
              อย่าปิดหน้านี้ก่อนกดบันทึก
            </MDTypography>
          )}
          <MDButton
            size='small'
            variant='text'
            color='error'
            onClick={() =>
              setConfirmBox({
                title: 'ยกเลิกที่แก้ไว้?',
                text: 'ข้อความที่แก้จะถูกแทนด้วยข้อมูลที่บันทึกล่าสุด',
                okLabel: 'ยกเลิกที่แก้',
                onOk: () => {
                  setAttr(base);
                  setRestored(false);
                },
              })
            }
          >
            ยกเลิกที่แก้
          </MDButton>
        </MDBox>
      )}

      <Grid container spacing={3}>
        {/* ── ซ้าย: เนื้อหาการตลาด ─────────────────────────────────────── */}
        <Grid item xs={12}>
          <MDBox sx={sectionSx}>
            <MDBox
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='wrap'
              gap={1}
            >
              {eyebrow('เนื้อหาการตลาด')}
              <MDButton
                variant='outlined'
                color='info'
                size='small'
                onClick={suggest}
                disabled={!canEdit || loading || loadedKey !== draftKey}
              >
                <Icon>auto_fix_high</Icon>&nbsp;ดึงจากข้อมูลสินค้า
              </MDButton>
            </MDBox>

            <MDBox mt={2.5} display='flex' flexDirection='column' gap={3}>
              <MDInput
                label='คำบรรยายเชิงการตลาด'
                placeholder='เช่น ยาสีฟันสมุนไพร สูตรเย็น กลิ่นสะระแหน่ ใช้ได้ทั้งครอบครัว'
                fullWidth
                multiline
                rows={4}
                value={attr.marketing_desc || ''}
                disabled={!canEdit}
                InputProps={{ disabled: !canEdit }}
                onChange={(e) => setAttr({ ...attr, marketing_desc: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />

              <MDInput
                label='จุดขาย'
                placeholder='เช่น ไม่มีแอลกอฮอล์ · ไม่มีน้ำตาล · ได้ อย. แล้ว'
                fullWidth
                multiline
                rows={2}
                value={attr.selling_points || ''}
                disabled={!canEdit}
                InputProps={{ disabled: !canEdit }}
                onChange={(e) => setAttr({ ...attr, selling_points: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />

              <MDBox>
                <MDInput
                  label='คำค้น'
                  placeholder='คั่นด้วยเว้นวรรค เช่น ยาสีฟัน สมุนไพร เย็นสดชื่น'
                  fullWidth
                  value={attr.keywords || ''}
                  disabled={!canEdit}
                  onChange={(e) => setAttr({ ...attr, keywords: e.target.value })}
                  inputProps={{ maxLength: 255, disabled: !canEdit }}
                  InputLabelProps={{ shrink: true }}
                />
                {(attr.keywords || '').trim() !== '' && (
                  <MDBox mt={1.5} display='flex' gap={0.75} flexWrap='wrap'>
                    {attr.keywords
                      .trim()
                      .split(/\s+/)
                      .slice(0, 12)
                      .map((word) => (
                        <Chip key={word} size='small' variant='outlined' label={word} />
                      ))}
                  </MDBox>
                )}
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 2.5 }} />
            <MDBox
              display='flex'
              alignItems='center'
              justifyContent='flex-end'
              gap={2}
              flexWrap='wrap'
            >
              <MDTypography variant='caption' color='text'>
                {canEdit
                  ? attr.updated_at
                    ? `แก้ไขล่าสุด ${Func.dateTimeDisplay(
                        String(attr.updated_at).replace(' ', 'T')
                      )}`
                    : 'ยังไม่เคยบันทึก'
                  : 'บัญชีนี้ดูได้อย่างเดียว'}
              </MDTypography>
              {canEdit && (
                <MDButton
                  variant='gradient'
                  color='dark'
                  onClick={save}
                  disabled={loading || loadedKey !== draftKey}
                  sx={{ minWidth: 160 }}
                >
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </Grid>

        {/* ── แคมเปญที่เกี่ยวข้อง (อ่านอย่างเดียว) ─────────────────────── */}
        <Grid item xs={12}>
          <MDBox sx={sectionSx}>
            <MDBox
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='wrap'
              gap={1}
              mb={1}
            >
              {eyebrow('แคมเปญที่เกี่ยวข้อง')}
              {expiredTotal > 0 && (
                <MDButton
                  size='small'
                  variant='text'
                  color='info'
                  onClick={() => setShowExpired(!showExpired)}
                >
                  {showExpired ? 'ซ่อนที่หมดอายุ' : `หมดอายุแล้ว ${expiredTotal}`}
                </MDButton>
              )}
            </MDBox>

            {camps === null && (
              <MDTypography variant='caption' color='text'>
                กำลังโหลด…
              </MDTypography>
            )}
            {camps && camps.failed && (
              <MDBox display='flex' alignItems='center' gap={1}>
                <MDTypography variant='caption' color='error'>
                  โหลดแคมเปญไม่สำเร็จ
                </MDTypography>
                <MDButton size='small' variant='text' color='info' onClick={loadCampaigns}>
                  ลองใหม่
                </MDButton>
              </MDBox>
            )}
            {camps && !camps.failed && current.length === 0 && (
              <MDTypography variant='caption' color='text'>
                ไม่มีแคมเปญที่ใช้อยู่
              </MDTypography>
            )}
            {current.map(campaignRow)}
            {showExpired && camps && !camps.failed && (
              <MDBox sx={{ opacity: 0.75 }}>
                {camps.expired.map(campaignRow)}
                {expiredTotal > camps.expired.length && (
                  <MDTypography variant='caption' color='text'>
                    แสดง {camps.expired.length} ล่าสุดจาก {expiredTotal}
                  </MDTypography>
                )}
              </MDBox>
            )}
          </MDBox>
        </Grid>
      </Grid>

      {notify}

      <Dialog
        open={Boolean(confirmBox)}
        onClose={() => setConfirmBox(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{confirmBox ? confirmBox.title : ''}</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>{confirmBox ? confirmBox.text : ''}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            onClick={() => setConfirmBox(null)}
          >
            ยกเลิก
          </MDButton>
          <MDButton
            variant='gradient'
            color='dark'
            onClick={() => {
              const box = confirmBox;
              setConfirmBox(null);
              if (box) box.onOk();
            }}
          >
            {confirmBox ? confirmBox.okLabel : ''}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

export default ProductAiInfo;
